import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Icons, QueryErrorBanner } from '../shared/ui';
import api from '../utils/api';

const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'vi-VN', {
    style: 'currency', currency: 'VND',
  }).format(n ?? 0);

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatYM = (ym, lang) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(
    lang === 'en' ? 'en-US' : 'vi-VN',
    { month: 'long', year: 'numeric' },
  );
};

const STATUS_STYLE = {
  confirmed: 'text-green-700 bg-green-50 ring-green-200',
  partial:   'text-amber-700 bg-amber-50  ring-amber-200',
  pending:   'text-teal-700  bg-teal-50   ring-teal-200',
  rejected:  'text-red-700   bg-red-50    ring-red-200',
};
const STATUS_ICON = { confirmed: '✓', partial: '½', pending: '⋯', rejected: '✗' };

// Normalise category keys → canonical CAT_META key.
// Keys are lowercased before lookup so matching is case-insensitive.
// Covers English aliases, Vietnamese display names, and underscore/space variants.
const CAT_NORMALIZE = {
  // English aliases
  'electric':        'electricity',
  // Vietnamese → canonical English
  'điện':            'electricity',
  'tiền điện':       'electricity',
  'nước':            'water',
  'tiền nước':       'water',
  'thang máy':       'elevator',
  'vệ sinh':         'cleaning',
  'giặt đồ':         'washing',
  'xe máy':          'parking_motorbike',
  'ô tô':            'parking_car',
  'sửa chữa':        'repair',
  'bảo trì':         'maintenance',
  'bảo_trì':         'maintenance',
  'bao_tri':         'maintenance',
  'bao tri':         'maintenance',
  'quản lý':         'management',
  'bảo hiểm':        'insurance',
  'an ninh':         'security',
  'thuế':            'tax',
  'thuế phí':        'tax',
  'pháp lý':         'legal',
  'hoa hồng':        'commission',
  'khác':            'other',
};

const CAT_META = {
  electricity:       { icon: '⚡', label: 'Điện',             group: 'meter' },
  water:             { icon: '💧', label: 'Nước',              group: 'meter' },
  internet:          { icon: '📡', label: 'Internet',          group: 'service' },
  elevator:          { icon: '🏢', label: 'Thang máy',         group: 'service' },
  cleaning:          { icon: '🧹', label: 'Vệ sinh',           group: 'service' },
  washing:           { icon: '👕', label: 'Giặt đồ',           group: 'service' },
  parking_motorbike: { icon: '🛵', label: 'Xe máy',            group: 'service' },
  parking_car:       { icon: '🚗', label: 'Ô tô',              group: 'service' },
  // fallback when other_costs_detail is null — lump-sum services income
  other_service:     { icon: '🔧', label: 'Dịch vụ khác',      group: 'service' },
  repair:            { icon: '🔧', label: 'Sửa chữa',          group: 'overhead' },
  maintenance:       { icon: '🏗️', label: 'Bảo trì',         group: 'overhead' },
  management:        { icon: '📋', label: 'Quản lý',           group: 'overhead' },
  insurance:         { icon: '🛡️', label: 'Bảo hiểm',       group: 'overhead' },
  security:          { icon: '🔒', label: 'An ninh',           group: 'overhead' },
  tax:               { icon: '📊', label: 'Thuế phí',          group: 'overhead' },
  legal:             { icon: '⚖️', label: 'Pháp lý',         group: 'overhead' },
  commission:        { icon: '💼', label: 'Hoa hồng',          group: 'overhead' },
  other:             { icon: '📦', label: 'Khác',              group: 'overhead' },
};

const BREAKDOWN_GROUPS = [
  { key: 'meter',    label: 'Tiện ích đồng hồ',  sub: 'Điện & nước — thu từ khách thuê, chi cho nhà cung cấp' },
  { key: 'service',  label: 'Dịch vụ khác',       sub: 'Internet, vệ sinh, thang máy…' },
  { key: 'overhead', label: 'Chi phí vận hành',   sub: 'Sửa chữa, bảo trì — chủ nhà chi, không thu từ khách' },
];

// Normalize a raw DB category key → canonical CAT_META key.
// Tries exact match, then case-insensitive (Vietnamese display names use mixed case).
const normCat = (raw) => {
  if (!raw) return 'other';
  const s = String(raw).trim();
  return CAT_NORMALIZE[s]
    ?? CAT_NORMALIZE[s.toLowerCase()]
    ?? (CAT_META[s] ? s : s.toLowerCase())  // already canonical, or lowercase fallback
    ?? s;
};

export default function FinanceBillingReport({ buildings = [] }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';

  const [selectedMonth, setSelectedMonth]     = useState(nowYM);
  const [selectedBldId, setSelectedBldId]     = useState('');
  const [expandRooms, setExpandRooms]         = useState(false);

  const payParams = useMemo(() => {
    const p = new URLSearchParams({ month: selectedMonth });
    if (selectedBldId) p.set('building_id', selectedBldId);
    return p.toString();
  }, [selectedMonth, selectedBldId]);

  const expParams = useMemo(() => {
    const p = new URLSearchParams({ billing_month: selectedMonth });
    if (selectedBldId) p.set('building_id', selectedBldId);
    return p.toString();
  }, [selectedMonth, selectedBldId]);

  const { data: payments = [], isLoading: pmtLoading, isError: pmtError, refetch: refetchPmt } = useQuery({
    queryKey: ['billing-report-payments', selectedMonth, selectedBldId],
    queryFn: () => api.get(`/api/admin/payments?${payParams}`).then(r => r.data ?? []),
    staleTime: 2 * 60 * 1000,
  });

  const { data: expenses = [], isLoading: expLoading, isError: expError, refetch: refetchExp } = useQuery({
    queryKey: ['billing-report-expenses', selectedMonth, selectedBldId],
    queryFn: () => api.get(`/api/expenses?${expParams}`).then(r => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.expenses ?? []);
    }),
    staleTime: 60 * 1000,
  });

  const isLoading = pmtLoading || expLoading;
  const selectedBuilding = buildings.find(b => String(b.id) === String(selectedBldId));

  // Billing cycle metadata
  const cycle = useMemo(() => {
    const payMethod  = selectedBuilding?.payment_method ?? 'pay_first';
    const billingDay = selectedBuilding?.billing_day ?? 5;
    return { payMethod, billingDay, invoiceMonth: shiftMonth(selectedMonth, 1) };
  }, [selectedMonth, selectedBuilding]);

  // Revenue aggregation from payment records (billing_month = selectedMonth)
  const rev = useMemo(() => {
    const total     = payments.reduce((s, p) => s + (p.invoice_amount ?? 0), 0);
    const collected = payments.reduce((s, p) => s + (p.paid_amount ?? 0), 0);
    const rent      = payments.reduce((s, p) => s + (p.base_rent ?? 0), 0);
    const electric  = payments.reduce((s, p) => s + (p.electric_cost ?? 0), 0);
    const water     = payments.reduce((s, p) => s + (p.water_cost ?? 0), 0);
    const services  = payments.reduce((s, p) => s + (p.other_costs ?? 0), 0);
    const pending   = payments.filter(p => p.status === 'pending').length;
    const rate      = total > 0 ? Math.round((collected / total) * 100) : 0;
    return { total, collected, rent, electric, water, services, pending, rate };
  }, [payments]);

  // Full category breakdown — normalises expense category keys so 'electric' === 'electricity'
  const breakdown = useMemo(() => {
    // Income: meter costs (electric / water) come directly from payment columns
    const incomeMap = {
      electricity: payments.reduce((s, p) => s + (p.electric_cost ?? 0), 0),
      water:       payments.reduce((s, p) => s + (p.water_cost ?? 0), 0),
    };
    // Income: other services from other_costs_detail JSONB {internet: 300000, cleaning: 150000, ...}
    // Track how much detail we could extract vs. total other_costs, so we can show a fallback
    // "Dịch vụ khác" row when other_costs_detail is null or incomplete.
    let detailedServicesTotal = 0;
    for (const p of payments) {
      const ocd = p.other_costs_detail;
      if (ocd && typeof ocd === 'object' && Object.keys(ocd).length > 0) {
        for (const [k, v] of Object.entries(ocd)) {
          const key = normCat(k);
          if (v > 0) {
            incomeMap[key] = (incomeMap[key] ?? 0) + v;
            detailedServicesTotal += v;
          }
        }
      }
    }
    // Fallback: any other_costs not explained by detail → lump under 'other_service'
    const totalOtherCosts   = payments.reduce((s, p) => s + (p.other_costs ?? 0), 0);
    const undistributed     = totalOtherCosts - detailedServicesTotal;
    if (undistributed > 1) {
      incomeMap['other_service'] = (incomeMap['other_service'] ?? 0) + undistributed;
    }

    // Expenses: normalise keys so DB 'electric' → 'electricity', etc.
    const expMap = {};
    for (const e of expenses) {
      const key = normCat(e.category);
      if ((e.amount ?? 0) > 0) expMap[key] = (expMap[key] ?? 0) + (e.amount ?? 0);
    }

    // Union of all categories with any data
    const allCats = new Set([
      ...Object.keys(incomeMap).filter(k => incomeMap[k] > 0),
      ...Object.keys(expMap),
    ]);

    const rows = Array.from(allCats).map(cat => {
      const meta = CAT_META[cat] ?? { icon: '📦', label: cat, group: 'overhead' };
      return {
        cat,
        icon:    meta.icon,
        label:   meta.label,
        group:   meta.group,
        income:  incomeMap[cat] ?? 0,
        expense: expMap[cat] ?? 0,
        margin:  (incomeMap[cat] ?? 0) - (expMap[cat] ?? 0),
      };
    });

    const maxVal  = Math.max(...rows.map(r => Math.max(r.income, r.expense)), 1);
    const byGroup = g => rows.filter(r => r.group === g).sort((a, b) => (b.income + b.expense) - (a.income + a.expense));

    return {
      meter:    byGroup('meter'),
      service:  byGroup('service'),
      overhead: byGroup('overhead'),
      maxVal,
      hasData:  rows.length > 0,
    };
  }, [payments, expenses]);

  // Expense total (uses normalised map, consistent with breakdown)
  const expTotal = useMemo(() => expenses.reduce((s, e) => s + (e.amount ?? 0), 0), [expenses]);

  const netPL = rev.total - expTotal;

  const fieldCls = 'h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  return (
    <div className="space-y-4">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-lg p-1">
          <button
            onClick={() => setSelectedMonth(m => shiftMonth(m, -1))}
            className="w-7 h-7 flex items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 transition-colors"
          >
            <Icons.ChevronLeft size={14} />
          </button>
          <span className="px-2 text-[13px] font-semibold text-stone-800 min-w-[130px] text-center">
            {formatYM(selectedMonth, lang)}
          </span>
          <button
            onClick={() => setSelectedMonth(m => shiftMonth(m, 1))}
            disabled={selectedMonth >= nowYM()}
            className="w-7 h-7 flex items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icons.ChevronRight size={14} />
          </button>
        </div>

        {buildings.length > 1 && (
          <select value={selectedBldId} onChange={e => setSelectedBldId(e.target.value)} className={fieldCls}>
            <option value="">Tất cả tòa nhà</option>
            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}

        <div className="flex-1" />
        {!isLoading && (
          <span className="text-[11px] text-stone-400">
            {payments.length} phòng · {expenses.length} khoản chi
          </span>
        )}
      </div>

      {/* ── Billing cycle info banner ── */}
      <div className="bg-teal-50 border border-teal-100 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <CycleTag label="Kỳ sử dụng"        value={selectedMonth} />
        <div className="w-px h-3 bg-teal-200 hidden sm:block" />
        <CycleTag label="Hóa đơn điện/nước" value={`01/${cycle.invoiceMonth.replace('-', '/')}`} />
        <div className="w-px h-3 bg-teal-200 hidden sm:block" />
        <CycleTag label="Hạn thanh toán"    value={`${cycle.billingDay}/${cycle.invoiceMonth.replace('-', '/')}`} />
        <div className="w-px h-3 bg-teal-200 hidden sm:block" />
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
          cycle.payMethod === 'pay_first' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {cycle.payMethod === 'pay_first' ? 'Thu trước' : 'Thu sau'}
        </span>
        <span className="text-[11px] text-teal-600 italic hidden lg:block">
          Điện/nước kỳ {selectedMonth} → thu trong tháng {cycle.invoiceMonth}
        </span>
      </div>

      {(pmtError || expError) && (
        <QueryErrorBanner onRetry={() => { refetchPmt(); refetchExp(); }} />
      )}

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Doanh thu kỳ"   value={VND(rev.total, lang)}   sub={`${payments.length} phòng`}          color="teal"                      loading={isLoading} />
        <KpiCard label="Đã thu"          value={VND(rev.collected, lang)} sub={`${rev.rate}% tỷ lệ thu`}            color="green"                     loading={isLoading} />
        <KpiCard label="Chi phí kỳ"      value={VND(expTotal, lang)}    sub={`${expenses.length} khoản`}           color="red"                       loading={isLoading} />
        <KpiCard
          label="Lợi nhuận ròng"
          value={`${netPL >= 0 ? '+' : ''}${VND(netPL, lang)}`}
          sub={netPL >= 0 ? 'Có lãi' : 'Lỗ kỳ này'}
          color={netPL >= 0 ? 'emerald' : 'red'}
          loading={isLoading}
        />
      </div>

      {/* ── Revenue + Expense side-by-side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Revenue table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <SectionHeader
            title={`Doanh thu — kỳ ${selectedMonth}`}
            sub="Theo billing_month, độc lập với ngày thu thực tế"
          />
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-stone-50">
                <ColHead>Danh mục</ColHead>
                <ColHead right>Kỳ vọng</ColHead>
                <ColHead center>Thời điểm thu</ColHead>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              <RevRow icon="🏠" label="Tiền thuê"     value={rev.rent}     timing="same" invoiceMonth={cycle.invoiceMonth} lang={lang} />
              <RevRow icon="⚡" label="Tiền điện"     value={rev.electric} timing="next" invoiceMonth={cycle.invoiceMonth} lang={lang} />
              <RevRow icon="💧" label="Tiền nước"     value={rev.water}    timing="next" invoiceMonth={cycle.invoiceMonth} lang={lang} />
              <RevRow icon="🔧" label="Dịch vụ khác" value={rev.services} timing="same" invoiceMonth={cycle.invoiceMonth} lang={lang} />
              <tr className="bg-stone-50 border-t border-stone-200 font-semibold">
                <td className="px-4 py-2.5 text-stone-700">Tổng cộng</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-teal-700">{isLoading ? '…' : VND(rev.total, lang)}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className="text-[10px] font-bold text-stone-600">{rev.rate}% đã thu</span>
                </td>
              </tr>
            </tbody>
          </table>
          {rev.total - rev.collected > 0 && (
            <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-[11px] text-amber-700">
              Còn lại: <span className="font-semibold">{VND(rev.total - rev.collected, lang)}</span>
              {' '}· {rev.pending} phòng chờ xác nhận
            </div>
          )}
        </div>

        {/* Expense table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <SectionHeader
            title={`Chi phí — kỳ ${selectedMonth}`}
            sub="Theo billing_month — ngày thực chi có thể khác kỳ"
          />
          {expenses.length === 0 && !expLoading ? (
            <div className="px-4 py-10 text-center text-[12px] text-stone-400">
              Chưa có chi phí nào cho kỳ này
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-stone-50">
                  <ColHead>Mô tả</ColHead>
                  <ColHead right>Số tiền</ColHead>
                  <ColHead center>Ngày chi</ColHead>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {expenses.map(exp => (
                  <tr key={exp.id} className="hover:bg-stone-50">
                    <td className="px-4 py-2.5 text-stone-700 max-w-[180px]">
                      <span className="truncate block" title={exp.title}>{exp.title}</span>
                      {exp.category && (
                        <span className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                          {CAT_META[normCat(exp.category)]?.label ?? exp.category}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-red-600">
                      {VND(exp.amount, lang)}
                    </td>
                    <td className="px-4 py-2.5 text-center text-stone-500">
                      {exp.expense_date
                        ? new Date(exp.expense_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
                        : '—'}
                    </td>
                  </tr>
                ))}
                <tr className="bg-stone-50 border-t border-stone-200 font-semibold">
                  <td className="px-4 py-2.5 text-stone-700">Tổng chi</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-red-600">{isLoading ? '…' : VND(expTotal, lang)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Full cost breakdown by category ── */}
      {breakdown.hasData && (
        <CostBreakdownSection
          breakdown={breakdown}
          selectedMonth={selectedMonth}
          lang={lang}
          loading={isLoading}
        />
      )}

      {/* ── Room breakdown ── */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <div>
            <p className="text-[13px] font-semibold text-stone-700">Chi tiết theo phòng</p>
            <p className="text-[11px] text-stone-400 mt-0.5">{payments.length} phòng trong kỳ {selectedMonth}</p>
          </div>
          {payments.length > 8 && (
            <button
              onClick={() => setExpandRooms(v => !v)}
              className="text-[12px] text-teal-600 hover:text-teal-700 font-medium"
            >
              {expandRooms ? 'Thu gọn' : 'Mở rộng'}
            </button>
          )}
        </div>

        {payments.length === 0 && !pmtLoading ? (
          <div className="px-4 py-8 text-center text-[12px] text-stone-400">
            Không có dữ liệu thanh toán cho kỳ này
          </div>
        ) : !expandRooms && payments.length > 8 ? (
          <div className="px-4 py-3 bg-stone-50 text-center text-[12px] text-stone-500">
            {payments.length} phòng — nhấn "Mở rộng" để xem chi tiết từng phòng
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-stone-50">
                  <ColHead>Phòng</ColHead>
                  <ColHead>Khách thuê</ColHead>
                  <ColHead right>Thuê</ColHead>
                  <ColHead right>Điện</ColHead>
                  <ColHead right>Nước</ColHead>
                  <ColHead right>Tổng HĐ</ColHead>
                  <ColHead center>TT</ColHead>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {payments.map(p => (
                  <tr key={p.id} className="hover:bg-stone-50">
                    <td className="px-4 py-2.5 font-medium text-stone-800">{p.room_number}</td>
                    <td className="px-4 py-2.5 text-stone-500 max-w-[120px] truncate">{p.renter_name}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-stone-700">{VND(p.base_rent, lang)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-stone-500">
                      {p.electric_cost > 0 ? VND(p.electric_cost, lang) : <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-stone-500">
                      {p.water_cost > 0 ? VND(p.water_cost, lang) : <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-stone-800">
                      {VND(p.invoice_amount, lang)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${STATUS_STYLE[p.status] ?? 'text-stone-600 bg-stone-100 ring-stone-200'}`}>
                        {STATUS_ICON[p.status] ?? p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CostBreakdownSection ───────────────────────────────────────────────────────

function CostBreakdownSection({ breakdown, selectedMonth, lang, loading }) {
  const totalIncome  = [...breakdown.meter, ...breakdown.service].reduce((s, r) => s + r.income, 0);
  const totalExpense = [...breakdown.meter, ...breakdown.service, ...breakdown.overhead].reduce((s, r) => s + r.expense, 0);
  const totalMargin  = totalIncome - totalExpense;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <SectionHeader
        title="Phân tích Thu/Chi theo danh mục"
        sub={`Kỳ ${selectedMonth} — thu từ khách thuê so với chi phí thực tế của chủ nhà`}
      />
      <div className="divide-y divide-stone-50">
        {BREAKDOWN_GROUPS.map(g => {
          const rows = breakdown[g.key];
          if (!rows.length) return null;
          return (
            <div key={g.key}>
              <div className="px-4 py-2 bg-stone-50 border-b border-stone-100">
                <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">{g.label}</span>
                <span className="text-[10px] text-stone-400 ml-2 normal-case">{g.sub}</span>
              </div>
              {rows.map(row => (
                <BreakdownRow key={row.cat} row={row} maxVal={breakdown.maxVal} lang={lang} loading={loading} />
              ))}
            </div>
          );
        })}
      </div>
      {/* Summary footer */}
      <div className="px-4 py-3 bg-stone-50 border-t border-stone-200 flex flex-wrap items-center gap-x-6 gap-y-1.5">
        <Stat label="Tổng thu dịch vụ" value={VND(totalIncome, lang)}   valueColor="text-teal-700" bold />
        <Stat label="Tổng chi"          value={VND(totalExpense, lang)}  valueColor="text-red-600"  bold />
        <Stat
          label="Lợi nhuận dịch vụ"
          value={`${totalMargin >= 0 ? '+' : ''}${VND(totalMargin, lang)}`}
          valueColor={totalMargin >= 0 ? 'text-green-600' : 'text-red-600'}
          bold
        />
      </div>
    </div>
  );
}

function BreakdownRow({ row, maxVal, lang, loading }) {
  const incPct = Math.round((row.income  / maxVal) * 100);
  const expPct = Math.round((row.expense / maxVal) * 100);
  const pos    = row.margin >= 0;

  return (
    <div className="px-4 py-3 flex items-start gap-4 hover:bg-stone-50">
      {/* Label + bars */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="text-[12px] font-medium text-stone-700">
          <span className="mr-1.5">{row.icon}</span>{row.label}
        </div>
        {row.income > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-stone-400 w-7 shrink-0">Thu</span>
            <div className="flex-1 h-2.5 bg-stone-100 rounded-full overflow-hidden max-w-[200px]">
              <div className="h-full bg-teal-400 rounded-full transition-all" style={{ width: `${incPct}%` }} />
            </div>
            <span className="text-[11px] font-medium text-teal-700 tabular-nums w-28 text-right shrink-0">
              {loading ? '…' : VND(row.income, lang)}
            </span>
          </div>
        )}
        {row.expense > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-stone-400 w-7 shrink-0">Chi</span>
            <div className="flex-1 h-2.5 bg-stone-100 rounded-full overflow-hidden max-w-[200px]">
              <div className="h-full bg-red-400 rounded-full transition-all" style={{ width: `${expPct}%` }} />
            </div>
            <span className="text-[11px] font-medium text-red-600 tabular-nums w-28 text-right shrink-0">
              {loading ? '…' : VND(row.expense, lang)}
            </span>
          </div>
        )}
      </div>
      {/* Margin */}
      <div className="text-right shrink-0 pt-4">
        <div className={`text-[13px] font-bold tabular-nums ${pos ? 'text-green-600' : 'text-red-600'}`}>
          {pos ? '+' : ''}{loading ? '…' : VND(row.margin, lang)}
        </div>
        {row.income > 0 && row.expense > 0 && (
          <div className={`text-[10px] mt-0.5 ${pos ? 'text-green-500' : 'text-red-500'}`}>
            {Math.round((row.income / row.expense) * 100)}%
          </div>
        )}
      </div>
    </div>
  );
}

// ── tiny sub-components ──────────────────────────────────────────────────────

function CycleTag({ label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-[12px] text-teal-700">
      <span className="font-semibold">{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function KpiCard({ label, value, sub, color, loading }) {
  const colorCls = {
    teal:    'text-teal-700',
    green:   'text-green-700',
    red:     'text-red-600',
    emerald: 'text-emerald-700',
  }[color] ?? 'text-stone-800';
  return (
    <div className="bg-white rounded-xl border border-stone-200 px-4 py-3">
      <p className="text-[11px] font-medium text-stone-500 mb-1">{label}</p>
      <p className={`text-[15px] font-bold tabular-nums ${colorCls}`}>{loading ? '…' : value}</p>
      <p className="text-[11px] text-stone-400 mt-0.5">{sub}</p>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="px-4 py-3 border-b border-stone-100">
      <p className="text-[13px] font-semibold text-stone-700">{title}</p>
      {sub && <p className="text-[11px] text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ColHead({ children, right, center }) {
  return (
    <th className={`px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-stone-500 ${right ? 'text-right' : center ? 'text-center' : 'text-left'}`}>
      {children}
    </th>
  );
}

function RevRow({ icon, label, value, timing, invoiceMonth, lang }) {
  const nmLabel = invoiceMonth ? `T/${invoiceMonth.split('-')[1]}` : 'tháng sau';
  return (
    <tr className="hover:bg-stone-50">
      <td className="px-4 py-2.5 text-stone-700">
        <span className="mr-1.5">{icon}</span>{label}
        {timing === 'next' && (
          <span className="ml-1.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">thu {nmLabel}</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-stone-800">
        {value > 0 ? VND(value, lang) : <span className="text-stone-300">—</span>}
      </td>
      <td className="px-4 py-2.5 text-center">
        {value > 0 ? (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${timing === 'next' ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'}`}>
            {timing === 'next' ? nmLabel : 'trong kỳ'}
          </span>
        ) : (
          <span className="text-[10px] text-stone-300">—</span>
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, valueColor, bold }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] text-stone-500">{label}:</span>
      <span className={`text-[13px] tabular-nums ${valueColor ?? 'text-stone-800'} ${bold ? 'font-bold' : 'font-semibold'}`}>{value}</span>
    </div>
  );
}
